import * as React from "react";
import Select, {
  SelectInstance,
  SingleValue,
  StylesConfig,
} from "react-select";
import { useState, useEffect, useRef } from "react";

export interface DropdownOption {
  value: string;
  label: string;
}

interface Props {
  options: DropdownOption[];
  onChange?: (newValue: SingleValue<DropdownOption>) => void;
  autoFocus?: boolean;
  defaultMenuIsOpen?: boolean;
  placeholder?: string;
  inputElementId?: string;
  inputElementName?: string;
  initialValue?: string | null;
  isClearable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  className?: string;
  inputClassName?: string;
  slim?: boolean;
  value?: string;
}

const ReactDropdown = ({
  options,
  onChange,
  placeholder,
  defaultMenuIsOpen,
  autoFocus,
  inputElementId,
  inputElementName,
  initialValue,
  isClearable,
  isLoading,
  isDisabled,
  className,
  inputClassName,
  slim,
  value,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState(
    null as DropdownOption | null
  );

  // Right alignment is used when left alignment causes the menu to go off screen
  const [alignRight, setAlignRight] = useState(false);

  // Prevents the menu from potentially being shown off screen for a split second before being aligned
  const [hidden, setHidden] = useState(true);

  const selectRef = useRef<SelectInstance<DropdownOption>>(null);
  const menuObserver: React.MutableRefObject<IntersectionObserver | null> =
    useRef(null);

  React.useEffect(() => {
    let newValue = options.find((o) => o.value == value) ?? null;
    setSelectedOption(newValue);
  }, [value]);

  const onMenuOpen = () => {
    const observeOnScreen = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];

      // Intersection ratio is < 1 if it's at least partially off screen
      if (entry.intersectionRatio < 1) {
        setAlignRight(true);
      }

      setHidden(false);
    };

    setTimeout(() => {
      if (selectRef.current == null) {
        setHidden(false);
        return;
      }

      const menuList = selectRef.current.menuListRef;

      // Should never be true in this case. Is only ever true if intersection observer has a parent
      if (menuList == null) {
        setHidden(false);
        return;
      }

      menuObserver.current = new IntersectionObserver(observeOnScreen);
      menuObserver.current.observe(menuList);
    }, 0);
  };

  const onMenuClose = () => {
    setAlignRight(false);
    setHidden(true);
    menuObserver.current?.disconnect();
  };

  useEffect(() => {
    const newValue =
      (value ?? initialValue) == null
        ? null
        : options.find((c) => c.value === (value ?? initialValue)) ?? null;

    setSelectedOption(newValue);
  }, [options]);

  const onOptionChanged = (newValue: SingleValue<DropdownOption>) => {
    setSelectedOption(newValue);
    onChange && onChange(newValue);
  };

  const createStyles = (): StylesConfig<DropdownOption, false> => ({
    control: (provided: any) => ({
      ...provided,
      width: "max-content",
      minWidth: "100%",
      ...(slim && { borderRadius: 0, minHeight: 0 }),
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      ...(slim && {
        fontSize: "0.725rem",
        padding: "0 4px",
      }),
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      ...(slim && { padding: "0 4px" }),
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      ...(slim && { padding: "0 4px" }),
    }),
    loadingIndicator: (provided: any) => ({
      ...provided,
      ...(slim && { padding: "0 4px" }),
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      ...(slim && { display: "none" }),
    }),
    input: (provided: any) => ({
      ...provided,
      ...(slim && { fontSize: "0.725rem", padding: 0, margin: 0 }),
    }),
    menu: (provided: any) => ({
      ...provided,
      width: "max-content",
      minWidth: "100%",
      ...(alignRight && { position: "absolute", right: 0 }),
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 10000,
      ...(hidden && { opacity: "0" }),
    }),
  });

  return (
    <div>
      <Select
        ref={selectRef}
        placeholder={placeholder}
        options={options}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onChange={onOptionChanged}
        defaultMenuIsOpen={defaultMenuIsOpen}
        autoFocus={autoFocus}
        value={selectedOption}
        isClearable={isClearable}
        isLoading={isLoading}
        isDisabled={isDisabled}
        menuPlacement="auto"
        menuPosition="absolute"
        menuPortalTarget={document.body}
        className={className}
        classNames={{ valueContainer: () => inputClassName || "" }}
        styles={createStyles()}
      />
      {inputElementId && (
        <input
          type="hidden"
          name={inputElementName}
          id={inputElementId}
          value={selectedOption?.value ?? ""}
        />
      )}
    </div>
  );
};

export default ReactDropdown;
